import { ReactNode, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Boxes, UtensilsCrossed, Wine } from 'lucide-react';
import { cn } from '../../../lib/utils';
import {
  primaryText,
  tertiaryText,
  semiBoldText,
  mediumText,
  smallTextSize,
  extraSmallTextSize,
  baseTextSize,
  greenTextColor,
  iconBgAmber,
  iconBgBlue,
  iconBgPurple,
} from '../../../styling/stylingHelper';
import { StatCard } from '../../ui/StatCard';
import { metricIcon } from '../../ui/metricIcons';
import { CardWithHeader } from '../../ui/CardWithHeader';
import { Callout } from '../../ui/Callout';
import { TableComponent } from '../../ui/TableComponent';
import { ResponsiveTable, MobileCardList, MobileCardField } from '../../shared/mobile/ResponsiveTable';
import { RestaurantFilterDropdown } from '../../ui/RestaurantFilterDropdown';
import { RestaurantLogo } from '../../ui/RestaurantLogo';
import { currencyFormatter } from '../../../services/helperService';
import { useRestaurantStore } from '../../../stores/appRestaurantStore';
import { useAppUserDefaultStore } from '../../../stores/appUserDefaultStore';
import { useInventoryOverviewStore } from '../../../stores/appInventoryOverviewStore';
import { useLoadInventoryOverview } from '../../../utils/dataLoadingUtils';
import { InventoryOverviewSessionRow } from '../../../services/inventory/inventoryService';
import { addPeriod, relativeDaysLabel, startOfPeriod } from '../../../utils/dateUtils';
import {
  InventoryGroupType,
  bucketFeedByWeek,
  buildOverviewKpis,
  classifyInventoryGroup,
  overviewRowKey,
  overviewWeekLabel,
} from './inventoryOverviewUtils';

const EMPTY_CELL = '—';
const MS_PER_DAY = 24 * 60 * 60 * 1000;

function shortDate(date: Date): string {
  return date.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
}

/** One rendered row — a real session, or a placeholder for a restaurant that never counted. */
interface OverviewTableRow {
  key: string;
  restaurantId: string;
  restaurantName: string;
  logoUrl: string | null;
  groupLabel: string;
  /** null = the never-counted placeholder row. */
  groupType: InventoryGroupType | null;
  completedAt: Date | null;
  countedByName: string | null;
  sectionCount: number | null;
  totalValue: number | null;
}

/**
 * Inventory page, "All Restaurants" mode: a read-only board answering
 * "which restaurant counted which inventory group, when, by whom, for what value?".
 *
 * Read-only by design — no time controls, no leakage / needs-review / below-minimum.
 * Those stay per-restaurant (clicking a row switches to that restaurant's page).
 */
const InventoryAllRestaurantsOverview = () => {
  const { t } = useTranslation();

  // Loaded here (not in InventoryPage) so the single-restaurant page is untouched.
  useLoadInventoryOverview();

  const restaurants = useRestaurantStore((s) => s.restaurants);
  const setRestaurant = useAppUserDefaultStore((s) => s.setRestaurant);

  const latest = useInventoryOverviewStore((s) => s.latest);
  const feed = useInventoryOverviewStore((s) => s.feed);
  const feedHasMore = useInventoryOverviewStore((s) => s.feedHasMore);
  const isLoading = useInventoryOverviewStore((s) => s.isLoading);
  const isLoadingMore = useInventoryOverviewStore((s) => s.isLoadingMore);
  const loadedOnce = useInventoryOverviewStore((s) => s.loadedOnce);
  const loadMoreFeed = useInventoryOverviewStore((s) => s.loadMoreFeed);

  // Local page state — empty = all restaurants. The feed is fetched unfiltered, so
  // changing this never refetches (same convention as the dashboard).
  const [selectedRestaurantIds, setSelectedRestaurantIds] = useState<string[]>([]);

  const restaurantById = useMemo(
    () => new Map(restaurants.map((r) => [r.id, r])),
    [restaurants]
  );

  const includesRestaurant = useMemo(() => {
    const selected = new Set(selectedRestaurantIds);
    return (id: string) => selected.size === 0 || selected.has(id);
  }, [selectedRestaurantIds]);

  const filteredLatest = useMemo(
    () => latest.filter((row) => includesRestaurant(row.restaurantId)),
    [latest, includesRestaurant]
  );
  const filteredFeed = useMemo(
    () => feed.filter((row) => includesRestaurant(row.restaurantId)),
    [feed, includesRestaurant]
  );

  const kpis = useMemo(() => buildOverviewKpis(filteredLatest), [filteredLatest]);

  const typeLabel = (type: InventoryGroupType): string =>
    type === 'food'
      ? t('inventory.overview.typeFood')
      : type === 'drinks'
      ? t('inventory.overview.typeDrinks')
      : t('inventory.overview.typeOther');

  const toRow = useMemo(
    () => (row: InventoryOverviewSessionRow): OverviewTableRow => {
      const restaurant = restaurantById.get(row.restaurantId);
      return {
        key: overviewRowKey(row),
        restaurantId: row.restaurantId,
        restaurantName: restaurant?.name ?? EMPTY_CELL,
        logoUrl: restaurant?.logoUrl ?? null,
        groupLabel: row.groupName ?? t('inventory.ungrouped'),
        groupType: classifyInventoryGroup(row.groupName),
        completedAt: row.completedAt,
        countedByName: row.countedByName,
        sectionCount: row.sectionCount,
        totalValue: row.totalValue,
      };
    },
    [restaurantById, t]
  );

  // Latest counts: one row per (restaurant, group), plus a placeholder for every
  // restaurant that has never counted — a customer that never counted must be
  // visible, not absent.
  const latestRows = useMemo(() => {
    const rows = filteredLatest.map(toRow);
    const counted = new Set(filteredLatest.map((row) => row.restaurantId));
    restaurants
      .filter((r) => includesRestaurant(r.id) && !counted.has(r.id))
      .forEach((r) => {
        rows.push({
          key: `never:${r.id}`,
          restaurantId: r.id,
          restaurantName: r.name,
          logoUrl: r.logoUrl,
          groupLabel: EMPTY_CELL,
          groupType: null,
          completedAt: null,
          countedByName: null,
          sectionCount: null,
          totalValue: null,
        });
      });
    return rows.sort(
      (a, b) =>
        a.restaurantName.localeCompare(b.restaurantName) || a.groupLabel.localeCompare(b.groupLabel)
    );
  }, [filteredLatest, restaurants, includesRestaurant, toRow]);

  const now = useMemo(() => new Date(), []);
  // Calendar-safe (DST) previous Monday — the same boundary bucketFeedByWeek splits on.
  const lastWeekDate = useMemo(() => addPeriod(startOfPeriod(now, 'week'), 'week', -1), [now]);
  const buckets = useMemo(() => bucketFeedByWeek(filteredFeed, now), [filteredFeed, now]);

  const openRestaurant = (row: OverviewTableRow) => {
    const restaurant = restaurantById.get(row.restaurantId);
    if (restaurant) setRestaurant(restaurant);
  };

  // "12 days ago" under the count date — same rule as the reports hub.
  const relativeLabel = (date: Date): string =>
    relativeDaysLabel(t, Math.max(0, Math.floor((now.getTime() - date.getTime()) / MS_PER_DAY)));

  const cellText = (value: ReactNode) => (
    <span className={cn(primaryText, smallTextSize, mediumText)}>{value}</span>
  );

  /** `showType` adds the muted Food/Drinks/Other label — latest table only. */
  const buildColumns = (showType: boolean) => [
    {
      key: 'restaurantName' as const,
      label: t('inventory.overview.colRestaurant'),
      render: (value: string, row: OverviewTableRow) => (
        <div className="flex items-center gap-2 min-w-0">
          <RestaurantLogo logoUrl={row.logoUrl} name={value} size="small" />
          <span className={cn(primaryText, smallTextSize, mediumText, 'truncate')}>{value}</span>
        </div>
      ),
    },
    {
      key: 'groupLabel' as const,
      label: t('inventory.overview.colGroup'),
      render: (value: string, row: OverviewTableRow) => (
        <div className="flex flex-col items-end">
          {cellText(value)}
          {showType && row.groupType ? (
            <span className={cn(tertiaryText, extraSmallTextSize, mediumText)}>
              {typeLabel(row.groupType)}
            </span>
          ) : null}
        </div>
      ),
    },
    {
      key: 'completedAt' as const,
      label: t('inventory.overview.colLastCount'),
      render: (value: Date | null) =>
        value ? (
          <div className="flex flex-col items-end">
            {cellText(shortDate(value))}
            <span className={cn(tertiaryText, extraSmallTextSize, mediumText)}>
              {relativeLabel(value)}
            </span>
          </div>
        ) : (
          <span className={cn(tertiaryText, smallTextSize, mediumText)}>
            {t('inventory.overview.neverCounted')}
          </span>
        ),
    },
    {
      key: 'countedByName' as const,
      label: t('inventory.overview.colCountedBy'),
      render: (value: string | null) => cellText(value || EMPTY_CELL),
    },
    {
      key: 'sectionCount' as const,
      label: t('inventory.overview.colSections'),
      render: (value: number | null) => cellText(value == null ? EMPTY_CELL : value),
    },
    {
      key: 'totalValue' as const,
      label: t('inventory.overview.colValue'),
      render: (value: number | null) =>
        cellText(value == null ? EMPTY_CELL : currencyFormatter.format(value)),
    },
  ];

  const renderMobileCard = (row: OverviewTableRow) => (
    <div
      className="bg-white dark:bg-card-dark rounded-xl p-4 shadow-[0px_4px_30px_0px_#4D546408] dark:shadow-[0px_4px_30px_0px_#00000040] cursor-pointer"
      onClick={() => openRestaurant(row)}
    >
      <div className="flex items-center justify-between gap-2 mb-3">
        <div className="flex items-center gap-2 min-w-0">
          <RestaurantLogo logoUrl={row.logoUrl} name={row.restaurantName} size="small" />
          <div className="flex flex-col min-w-0">
            <span className={cn(primaryText, smallTextSize, semiBoldText, 'truncate')}>
              {row.restaurantName}
            </span>
            <span className={cn(tertiaryText, extraSmallTextSize, mediumText, 'truncate')}>
              {row.groupLabel}
            </span>
          </div>
        </div>
        <span className={cn(primaryText, smallTextSize, semiBoldText, 'flex-shrink-0')}>
          {row.totalValue == null ? EMPTY_CELL : currencyFormatter.format(row.totalValue)}
        </span>
      </div>
      <div className="flex items-stretch border-t border-border dark:border-border-dark pt-3">
        <MobileCardField
          label={t('inventory.overview.colLastCount')}
          value={row.completedAt ? shortDate(row.completedAt) : t('inventory.overview.neverCounted')}
          divider
        />
        <MobileCardField
          label={t('inventory.overview.colCountedBy')}
          value={row.countedByName || EMPTY_CELL}
          divider
        />
        <MobileCardField
          label={t('inventory.overview.colSections')}
          value={row.sectionCount == null ? EMPTY_CELL : row.sectionCount}
        />
      </div>
    </div>
  );

  /** One table block (desktop table + mobile card list) used by every section below. */
  const renderTable = (
    title: string,
    rows: OverviewTableRow[],
    showType: boolean,
    footer?: ReactNode
  ) => (
    <ResponsiveTable
      desktop={
        <>
          <TableComponent<OverviewTableRow>
            title={title}
            data={rows}
            columns={buildColumns(showType)}
            blankLines={false}
            alignOtherColumnsRight
            itemsPerPage={Math.max(rows.length, 1)}
            onRowClick={openRestaurant}
            isRowClickable={(row) => restaurantById.has(row.restaurantId)}
          />
          {footer}
        </>
      }
      mobile={
        <div>
          <h3 className={cn(primaryText, 'text-[18px] leading-6 font-semibold mb-4')}>{title}</h3>
          <MobileCardList
            items={rows}
            keyOf={(row) => row.key}
            previewCount={rows.length}
            renderCard={renderMobileCard}
          />
          {footer}
        </div>
      }
    />
  );

  const loadMoreButton = feedHasMore ? (
    <div className="flex justify-center mt-4">
      <button
        type="button"
        onClick={() => loadMoreFeed()}
        disabled={isLoadingMore}
        className={cn(greenTextColor, semiBoldText, smallTextSize, 'py-1 disabled:opacity-60')}
      >
        {isLoadingMore ? t('inventory.loading') : t('inventory.overview.loadMore')}
      </button>
    </div>
  ) : null;

  /** This week / Last week always render — an empty "This week" is itself the signal. */
  const renderFeedSection = (title: string, rows: OverviewTableRow[], emptyText: string) =>
    rows.length === 0 ? (
      <CardWithHeader title={title}>
        <p className={cn(tertiaryText, baseTextSize, 'text-center w-full py-6')}>{emptyText}</p>
      </CardWithHeader>
    ) : (
      renderTable(title, rows, false)
    );

  if (restaurants.length === 0) {
    return <Callout text={t('inventory.overview.noRestaurants')} />;
  }

  const showOther = kpis.other > 0;
  // Filtered, so narrowing to a restaurant that never counted reads as "no counts yet".
  const hasAnyCounts = filteredLatest.length > 0 || filteredFeed.length > 0;

  return (
    <div>
      {/* Restaurant filter — empty selection = all restaurants */}
      <div className="mb-5 sm:mb-6 flex sm:justify-end">
        <RestaurantFilterDropdown
          restaurants={restaurants.map((r) => ({ id: r.id, name: r.name }))}
          selectedRestaurantIds={selectedRestaurantIds}
          setSelectedRestaurantIds={setSelectedRestaurantIds}
          hideColorSwatch
        />
      </div>

      {/* KPI row — no change pill: there is no previous period here. */}
      <div
        className={cn(
          'mb-5 sm:mb-6 grid gap-2.5 md:gap-6 grid-cols-2',
          showOther ? 'xl:grid-cols-4' : 'xl:grid-cols-3'
        )}
      >
        <StatCard
          {...metricIcon('revenue')}
          title={t('inventory.overview.kpiTotal')}
          mobileLayout="tile"
          value={kpis.total}
          valueType="currency"
          loading={isLoading}
          subtitle={t('inventory.overview.kpiLatestPerGroup')}
        />
        <StatCard
          icon={<UtensilsCrossed className="w-6 h-6" color="#6938EF" />}
          iconBg={iconBgPurple}
          title={t('inventory.overview.kpiFood')}
          mobileLayout="tile"
          value={kpis.food}
          valueType="currency"
          loading={isLoading}
          subtitle={t('inventory.overview.kpiGroupsCounted', { count: kpis.foodGroups })}
        />
        <StatCard
          icon={<Wine className="w-6 h-6" color="#2E90FA" />}
          iconBg={iconBgBlue}
          title={t('inventory.overview.kpiDrinks')}
          mobileLayout="tile"
          value={kpis.drinks}
          valueType="currency"
          loading={isLoading}
          subtitle={t('inventory.overview.kpiGroupsCounted', { count: kpis.drinksGroups })}
        />
        {showOther && (
          <StatCard
            icon={<Boxes className="w-6 h-6" color="#F59E0B" />}
            iconBg={iconBgAmber}
            title={t('inventory.overview.kpiOther')}
            mobileLayout="tile"
            value={kpis.other}
            valueType="currency"
            loading={isLoading}
            subtitle={t('inventory.overview.kpiGroupsCounted', { count: kpis.otherGroups })}
          />
        )}
      </div>

      {/* Latest count per restaurant × group */}
      <div className="mb-5 sm:mb-6">
        {renderTable(t('inventory.overview.latestHeading'), latestRows, true)}
      </div>

      {/* Full count feed, newest first, bucketed by Monday-based week */}
      <h2
        className={cn(
          primaryText,
          'text-[20px] sm:text-[24px] leading-7 sm:leading-[32px] font-semibold mb-3'
        )}
      >
        {t('inventory.overview.feedHeading')}
      </h2>
      {loadedOnce && !hasAnyCounts ? (
        <p className={cn(tertiaryText, baseTextSize, 'text-center py-6')}>
          {t('inventory.overview.noCountsYet')}
        </p>
      ) : (
        <div className="space-y-5 sm:space-y-6">
          {renderFeedSection(
            t('inventory.overview.thisWeek', { week: overviewWeekLabel(now) }),
            buckets.thisWeek.map(toRow),
            t('inventory.overview.noCountsThisWeek')
          )}
          {renderFeedSection(
            t('inventory.overview.lastWeek', { week: overviewWeekLabel(lastWeekDate) }),
            buckets.lastWeek.map(toRow),
            t('inventory.overview.noCountsLastWeek')
          )}
          {/* "Load more" sits under Earlier only — the server feed is one continuous
              keyset stream, and bucketing re-runs over the full loaded array. */}
          {buckets.earlier.length > 0
            ? renderTable(
                t('inventory.overview.earlier'),
                buckets.earlier.map(toRow),
                false,
                loadMoreButton
              )
            : loadMoreButton}
        </div>
      )}
    </div>
  );
};

export default InventoryAllRestaurantsOverview;
